import { useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Screen, useMonth } from '../ReportsApp'
import { downloadCsv, useReport } from '../api'
import { money, int, dayShort, monthName, pct, method, n, type Row } from '../format'
import { AsyncButton, Button, Card, Empty, MonthPicker, Table } from '../ui'
import type { PayoutsData } from './Payouts'

interface Statement {
  payee: string; consignor_key: string | null; month: string; from: string; to: string; consignor: Row | null; name: string; preferred_method: string | null
  rows: (Row & { sold_on: string; sale_no: number; channel: string; order: string | null; title: string; sku: string; list_price: number; sold_for: number; refunded: number; split_pct: number | null; share: number; fee: number })[]
  pieces: number; list_total: number; sold_total: number; share_total: number; fee_total: number
  paid_this_month: number; paid_all: number; balance: number
  floor: { pieces: number; list_value: number }
}

export function Statements() {
  const { month, setMonth, goLive } = useMonth()
  const { payee } = useParams()
  const nav = useNavigate()
  const list = useReport<PayoutsData>('payouts', { month })
  const people = (list.data?.people ?? []).filter((p) => p.consignor_key)
  // the first consignor with a balance opens by default
  useEffect(() => { if (!payee && people.length) nav(`/statements/${people[0].payee}`, { replace: true }) }, [payee, people, nav])
  const st = useReport<Statement>('statement', { payee: payee ?? '', month }, [payee], !!payee)
  const d = payee ? st.data : null
  const rows = d?.rows ?? []
  return (
    <Screen title="Consignor statements" sub="One page per consignor, for the month picked" right={<><MonthPicker month={month} onChange={setMonth} min={goLive.slice(0, 7)} /></>}>
      {(list.error || st.error) && <div className="rp-error">{list.error || st.error}</div>}
      <div className="rp-grid rp-grid--side">
        <Card>
          {!payee && <Empty>{list.loading ? 'Reading consignors…' : 'No consignor has a balance or a sale yet. Pick one on the right once there is.'}</Empty>}
          {payee && (
            <div className="rp-statement">
              <div className="rp-statement__head">
                <div>
                  <span className="rp-sub">Consignor statement</span>
                  <h2>{d?.name ?? '…'}{d?.consignor_key ? ` · consignor ${d.consignor_key}` : ''}</h2>
                  <span className="rp-sub">{monthName(month)}{d?.consignor?.default_split != null ? ` · split ${pct(d.consignor.default_split)} / ${pct(100 - n(d.consignor.default_split))}` : ''}{d?.preferred_method ? ` · payouts by ${method(d.preferred_method)}` : ''}</span>
                </div>
                <div style={{ textAlign: 'right' }}><img src="/brand/star-black.png" alt="Starchive" /><span className="rp-sub">Starchive · records from {dayShort(goLive)} {goLive.slice(0, 4)} onward</span></div>
              </div>
              <Table columns="60px 80px minmax(140px, 2fr) 90px 70px 80px 90px 90px" loading={st.loading} rows={rows} rowKey={(r, i) => `${r.sale_no}-${i}`} total
                empty={`Nothing sold for this consignor in ${monthName(month)}`}
                cols={[
                  { key: 'sold_on', label: 'Sold', render: (r) => dayShort(r.sold_on), total: `${int(rows.length)} ${rows.length === 1 ? 'piece' : 'pieces'}` },
                  { key: 'sale_no', label: 'Sale', render: (r) => r.channel !== 'counter' && r.order ? `Order ${r.order}` : `#${r.sale_no}` },
                  { key: 'title', label: 'Piece' },
                  { key: 'sku', label: 'SKU' },
                  { key: 'list_price', label: 'List', align: 'right', render: (r) => money(r.list_price, true), total: money(d?.list_total, true) },
                  { key: 'sold_for', label: 'Sold for', align: 'right', render: (r) => <>{money(n(r.sold_for) - n(r.refunded), true)}{n(r.refunded) > 0 && <span className="rp-muted"> ({money(r.refunded, true)} refunded)</span>}</>, total: money(d?.sold_total, true) },
                  { key: 'share', label: 'Your share', align: 'right', render: (r) => money(r.share), total: money(d?.share_total) },
                  { key: 'fee', label: 'Starchive fee', align: 'right', render: (r) => money(r.fee), total: money(d?.fee_total) },
                ]} />
              <div className="rp-kv" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', padding: '8px 0 0' }}>
                <div><span className="k">Your share this month</span><span className="v">{money(d?.share_total)}</span></div>
                <div><span className="k">Paid this month</span><span className="v">{money(d?.paid_this_month)}</span></div>
                <div><span className="k">Balance due (all months)</span><span className={'v' + (n(d?.balance) > 0 ? ' rp-tone-amber' : '')}>{money(d?.balance)}</span></div>
                <div><span className="k">Still on the floor</span><span className="v">{int(d?.floor.pieces)} {n(d?.floor.pieces) === 1 ? 'piece' : 'pieces'}</span><span className="s">{money(d?.floor.list_value, true)} list</span></div>
              </div>
              <p className="rp-sub" style={{ lineHeight: 1.5 }}>Sold-for is the price paid before sales tax. Your share is the sold-for price times your split. Pieces refunded in the month are shown net of the refund. Card sales are paid through Consignify; cash, Zelle and PayPal sales from the bank; crypto sales in crypto. Questions: hello@starchive.la.</p>
            </div>
          )}
        </Card>
        <div className="rp-no-print" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Card title="Consignors" note="Balance across every month">
            <div className="rp-list">
              {people.map((p) => <Link key={p.payee} to={`/statements/${p.payee}`} className={payee === p.payee ? 'is-active' : ''}><span>{p.consignor_key} · {p.name}{p.is_masih ? ' (Masih)' : ''}</span><b className={n(p.total) > 0 ? 'rp-tone-amber' : ''}>{money(p.total, true)}</b></Link>)}
              {!list.loading && people.length === 0 && <Empty>No consignor has sold anything yet</Empty>}
            </div>
            <p className="rp-note">Masih's own and Split pieces are on <Link to="/partner">Partner settlement</Link>.</p>
          </Card>
          {payee && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Button onClick={() => window.print()}>Print statement</Button>
              <AsyncButton run={() => downloadCsv('consignor_statement', { payee, from: d?.from, to: d?.to })} doneLabel="Downloaded">Export CSV</AsyncButton>
              <Button primary disabled={!d || n(d.balance) <= 0} onClick={() => nav(`/payouts/${payee}`)}>{d && n(d.balance) > 0 ? `Record payout ${money(d.balance)}` : 'Nothing to pay'}</Button>
            </div>
          )}
        </div>
      </div>
    </Screen>
  )
}
